window.AdminApplications = function AdminApplications({ showNotification }) {
  const [applications, setApplications] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('All');
  const [categoryFilter, setCategoryFilter] = React.useState('All');
  const [selectedApp, setSelectedApp] = React.useState(null);

  React.useEffect(() => {
    fetchApplications();
    const handleSync = () => {
      fetchApplications();
    };
    window.addEventListener('storage', handleSync);
    window.addEventListener('novatek_app_added', handleSync);
    return () => {
      window.removeEventListener('storage', handleSync);
      window.removeEventListener('novatek_app_added', handleSync);
    };
  }, []);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const res = await window.apiClient.get('applications.php');
      if (res.success) setApplications(res.data || []);
    } catch (e) {
      setApplications([
        {
          id: 1,
          ref_id: 'NV-CAREER-2026-X8K9L2',
          application_category: 'Start Your Journey Portal',
          job_title: 'Start Your Journey Portal',
          full_name: 'Dr. Rahul Sharma',
          email: 'rahul.sharma@engineering.in',
          phone: '+91 98220 11223',
          experience_years: '6',
          portfolio_url: 'https://linkedin.com/in/rahul-sharma',
          cover_note: 'Extensive CAD modeling in SolidWorks and finite element analysis.',
          resume_path: './public/uploads/resume_sample.pdf',
          cover_letter_path: './public/uploads/cover_sample.pdf',
          status: 'submitted',
          created_at: '2026-07-25 09:30:00'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewApp = async (app) => {
    setSelectedApp(app);
    if (app.status === 'submitted' || app.status === 'new') {
      try {
        await window.apiClient.put('applications.php', { id: app.id, status: 'under_review' });
        app.status = 'under_review';
        setSelectedApp({ ...app, status: 'under_review' });
        fetchApplications();
      } catch(e) {}
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await window.apiClient.put('applications.php', { id, status });
      showNotification(`Application status updated to '${status}'.`);
      fetchApplications();
      if (selectedApp && selectedApp.id === id) {
        setSelectedApp({ ...selectedApp, status });
      }
    } catch (err) {
      showNotification(err.message || 'Status update failed.', 'error');
    }
  };

  const handleDeleteApplication = async (id, name) => {
    if (!window.confirm(`Delete application record for '${name}'?`)) return;
    try {
      const res = await window.apiClient.delete('applications.php', { id });
      if (res && res.success !== false) {
        showNotification(`Application for '${name}' deleted successfully.`);
        if (selectedApp && (selectedApp.id === id || selectedApp.ref_id === id)) {
          setSelectedApp(null);
        }
        await fetchApplications();
      } else {
        showNotification(res.message || 'Deletion failed.', 'error');
      }
    } catch (err) {
      showNotification(err.message || 'Deletion failed.', 'error');
    }
  };

  const handleExportCSV = () => {
    window.open('/api/v1/applications.php?export=csv', '_blank');
  };

  const filteredApps = applications.filter((app) => {
    const appCat = app.application_category || (app.career_id ? 'Specific Job Opening' : 'Start Your Journey Portal');
    const matchesStatus = statusFilter === 'All' || app.status === statusFilter;
    const matchesCategory = categoryFilter === 'All' || appCat === categoryFilter;
    const matchesSearch = app.full_name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          app.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          app.ref_id.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesCategory && matchesSearch;
  });

  return (
    <>
      <div className="space-y-6 animate-fadeIn">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="font-serif text-2xl font-bold text-slate-900">Career Applications Portal</h1>
          <p className="font-sans text-xs text-slate-500">Review received applications from "Start Your Journey" portal & specific job openings, download resumes, shortlist candidates, or export reports.</p>
        </div>
        <button
          onClick={handleExportCSV}
          className="bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 font-mono text-xs font-bold uppercase px-4 py-2.5 rounded-lg shadow-xs transition-all flex items-center gap-2 cursor-pointer"
        >
          <span className="material-symbols-outlined text-base">download</span>
          <span>Export to Excel / CSV</span>
        </button>
      </div>

      {/* Search & Filter */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="relative w-full md:w-80">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base">search</span>
          <input
            type="text"
            placeholder="Search candidate name, email, or reference ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-900 outline-none focus:border-secondary font-mono"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-1.5 bg-slate-50 border border-slate-300 rounded text-xs text-slate-800 outline-none font-mono"
          >
            <option value="All">All Application Categories</option>
            <option value="Start Your Journey Portal">Start Your Journey Portal</option>
            <option value="Specific Job Opening">Specific Job Opening</option>
          </select>

          {/* Status Filter */}
          {['All', 'submitted', 'under_review', 'shortlisted', 'rejected'].map((st) => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              className={`px-3 py-1.5 rounded-md text-[11px] font-mono font-bold capitalize transition-all cursor-pointer ${
                statusFilter === st ? 'bg-secondary text-white shadow' : 'bg-slate-100 text-slate-600 border border-slate-200 hover:text-slate-900'
              }`}
            >
              {st.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-sans">
          <thead className="bg-slate-50 text-slate-500 font-mono text-[10px] uppercase border-b border-slate-200">
            <tr>
              <th className="p-4">REF ID</th>
              <th className="p-4">CATEGORY / SOURCE</th>
              <th className="p-4">CANDIDATE NAME & EMAIL</th>
              <th className="p-4">TARGET ROLE</th>
              <th className="p-4">EXPERIENCE</th>
              <th className="p-4">STATUS</th>
              <th className="p-4 text-center">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 font-mono">
            {filteredApps.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-8 text-center text-slate-400 font-mono">No career applications logged in system.</td>
              </tr>
            ) : (
              filteredApps.map((app) => {
                const isPortal = !app.career_id || app.application_category === 'Start Your Journey Portal';
                return (
                  <tr key={app.id} className="hover:bg-slate-50 transition-colors">
                    <td className="p-4 font-mono font-bold text-secondary">{app.ref_id}</td>
                    
                    <td className="p-4">
                      <span className={`font-mono text-[10px] font-bold px-2 py-0.5 rounded ${
                        isPortal ? 'bg-orange-50 text-secondary border border-orange-200' : 'bg-blue-50 text-blue-700 border border-blue-200'
                      }`}>
                        {isPortal ? 'Start Your Journey' : 'Job Opening'}
                      </span>
                    </td>

                    <td className="p-4">
                      <div className="font-bold text-slate-900 text-sm font-sans">{app.full_name}</div>
                      <div className="text-slate-500 font-mono text-[11px]">{app.email} | {app.phone || 'N/A'}</div>
                    </td>

                    <td className="p-4 font-serif text-slate-700">
                      {app.job_title || 'Start Your Journey (General)'}
                    </td>

                    <td className="p-4 font-mono text-slate-700">{app.experience_years ? app.experience_years + ' Yrs' : 'N/A'}</td>

                    <td className="p-4">
                      <span className={`font-mono text-[10px] font-bold px-2.5 py-1 rounded uppercase ${
                        app.status === 'shortlisted' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                        app.status === 'rejected' ? 'bg-red-50 text-red-700 border border-red-200' :
                        app.status === 'under_review' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}>
                        {app.status.replace('_', ' ')}
                      </span>
                    </td>

                    <td className="p-4 text-center">
                      <div className="flex justify-center gap-1.5">
                        <button
                          onClick={() => handleViewApp(app)}
                          className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-all border border-slate-200 cursor-pointer"
                          title="View Full Profile"
                        >
                          <span className="material-symbols-outlined text-sm">visibility</span>
                        </button>
                        <button
                          onClick={() => handleUpdateStatus(app.id, 'shortlisted')}
                          className="p-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg transition-all border border-emerald-200 cursor-pointer"
                          title="Shortlist Applicant"
                        >
                          <span className="material-symbols-outlined text-sm">check_circle</span>
                        </button>
                        <button
                          onClick={() => handleUpdateStatus(app.id, 'rejected')}
                          className="p-2 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg transition-all border border-amber-200 cursor-pointer"
                          title="Reject Applicant"
                        >
                          <span className="material-symbols-outlined text-sm">cancel</span>
                        </button>
                        <button
                          onClick={() => handleDeleteApplication(app.id, app.full_name)}
                          className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-all border border-red-200 cursor-pointer"
                          title="Delete Application"
                        >
                          <span className="material-symbols-outlined text-sm">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>
      </div>

      {/* Candidate Profile Detail Modal */}
      {selectedApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar p-6 sm:p-8 space-y-6 text-slate-800">
            
            {/* Modal Header */}
            <div className="flex justify-between items-start border-b border-slate-100 pb-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-slate-400 font-bold">{selectedApp.ref_id}</span>
                  <span className={`font-mono text-xs font-bold px-2.5 py-0.5 rounded-md uppercase ${
                    selectedApp.status === 'shortlisted' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                    selectedApp.status === 'rejected' ? 'bg-red-50 text-red-700 border border-red-200' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {selectedApp.status.replace('_', ' ')}
                  </span>
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl font-extrabold text-slate-900 pt-1">
                  {selectedApp.full_name}
                </h3>
              </div>
              <button 
                onClick={() => setSelectedApp(null)} 
                className="text-slate-400 hover:text-slate-800 p-2 hover:bg-slate-100 rounded-full transition-all cursor-pointer"
              >
                <span className="material-symbols-outlined text-2xl">close</span>
              </button>
            </div>

            {/* Modal Body */}
            <div className="space-y-5 font-sans">
              
              {/* 4 Tile Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 p-5 rounded-2xl border border-slate-200 font-mono">
                <div className="space-y-1">
                  <span className="text-slate-500 block text-[10px] tracking-wider uppercase font-semibold">EMAIL ADDRESS</span>
                  <span className="text-slate-900 text-sm font-bold block truncate">{selectedApp.email}</span>
                </div>
                <div className="space-y-1">
                  <span className="text-slate-500 block text-[10px] tracking-wider uppercase font-semibold">PHONE / MOBILE</span>
                  <span className="text-slate-900 text-sm font-bold block">{selectedApp.phone || 'Not Provided'}</span>
                </div>
                <div className="space-y-1 pt-1">
                  <span className="text-slate-500 block text-[10px] tracking-wider uppercase font-semibold">SOURCE CATEGORY</span>
                  <span className="text-secondary text-xs font-bold block">{selectedApp.application_category || 'Start Your Journey Portal'}</span>
                </div>
                <div className="space-y-1 pt-1">
                  <span className="text-slate-500 block text-[10px] tracking-wider uppercase font-semibold">EXPERIENCE</span>
                  <span className="text-slate-900 text-xs font-bold block">{selectedApp.experience_years ? selectedApp.experience_years + ' Years' : 'N/A'}</span>
                </div>
              </div>

              {/* LinkedIn / Portfolio Link */}
              {(selectedApp.linkedin_url || selectedApp.portfolio_url) && (
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-1">
                  <span className="font-mono text-[10px] text-slate-500 uppercase tracking-widest block font-semibold">LINKEDIN / PORTFOLIO LINK</span>
                  <a 
                    href={selectedApp.linkedin_url || selectedApp.portfolio_url} 
                    target="_blank" 
                    rel="noreferrer" 
                    className="text-secondary hover:underline font-mono text-xs font-semibold flex items-center gap-1.5 truncate"
                  >
                    <span className="material-symbols-outlined text-sm">link</span>
                    <span className="truncate">{selectedApp.linkedin_url || selectedApp.portfolio_url}</span>
                  </a>
                </div>
              )}

              {/* Message / Technical Summary */}
              {selectedApp.cover_note && (
                <div className="space-y-1.5">
                  <span className="font-mono text-[10px] text-slate-500 uppercase tracking-widest block font-semibold">MESSAGE / TECHNICAL SUMMARY</span>
                  <p className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-slate-700 text-xs leading-relaxed font-sans italic">
                    "{selectedApp.cover_note}"
                  </p>
                </div>
              )}

              {/* Action Toolbar */}
              <div className="pt-4 flex flex-wrap gap-3 items-center justify-between border-t border-slate-100">
                
                {/* Left Actions: Resume PDF & Delete Application */}
                <div className="flex flex-wrap items-center gap-2">
                  {selectedApp.resume_path && (
                    <a
                      href={selectedApp.resume_path}
                      target="_blank"
                      download
                      className="bg-secondary hover:bg-orange-600 text-white font-mono text-xs font-bold px-4 py-2.5 rounded-xl shadow-md transition-all flex items-center gap-2"
                    >
                      <span className="material-symbols-outlined text-base">picture_as_pdf</span>
                      <span>Resume PDF</span>
                    </a>
                  )}

                  {/* Delete Application Option */}
                  <button
                    onClick={() => handleDeleteApplication(selectedApp.id, selectedApp.full_name)}
                    className="bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 font-mono text-xs font-bold px-4 py-2.5 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer"
                  >
                    <span className="material-symbols-outlined text-base">delete</span>
                    <span>Delete Application</span>
                  </button>
                </div>

                {/* Right Actions: Shortlist / Reject */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleUpdateStatus(selectedApp.id, 'shortlisted')}
                    className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 font-mono text-xs font-bold px-4 py-2.5 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer"
                  >
                    <span className="material-symbols-outlined text-base">check_circle</span>
                    <span>Shortlist</span>
                  </button>
                  <button
                    onClick={() => handleUpdateStatus(selectedApp.id, 'rejected')}
                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 font-mono text-xs font-bold px-4 py-2.5 rounded-xl transition-all cursor-pointer"
                  >
                    <span>Reject</span>
                  </button>
                </div>

              </div>

            </div>

          </div>
        </div>
      )}
    </>
  );
};
